// @flow
/*
Time picker component that depends on a date in the form

While you can only use this to choose the time, this component will split apart
the datetime when receiving it from the context  and call onChange with a combined datetime.

*/
import * as React from 'react';
import { withSeconds } from 'utils/date';
import { isEmpty } from 'utils/functional';
import {
  combineFormDateTime,
  splitFormDateTime
} from 'utils/idealistdayevents';
import Input from 'components/Input';
import TimeInput from 'components/TimeInput';

type Props = {
  date: string, // YYYY-MM-DD
  value: ?string,
  onChange: (?string) => void,
  hasError?: boolean
};

const getTimeSupport = () => {
  const input = document.createElement('input');
  input.setAttribute('type', 'time');

  const notValid = 'not-a-time';
  input.setAttribute('value', notValid);

  return input.value !== notValid;
};

const parseDateParts = (datetimeStr: string) => {
  if (isEmpty(datetimeStr)) return {};
  const dateParts = datetimeStr.split('T');
  const timeParts = dateParts[1].split(':');
  return {
    date: dateParts[0],
    time: dateParts[1],
    timeNoMs: timeParts.slice(0, 2).join(':')
  };
};

type State = {
  timeSupported: boolean
};

export default class TimePicker extends React.Component<Props, State> {
  state = { timeSupported: true };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.date !== this.props.date) {
      if (this.props.value) {
        const { time } = splitFormDateTime(this.props.value);
        if (time) {
          this.props.onChange(combineFormDateTime(this.props.date, time));
        }
      }
    }
  }

  componentDidMount() {
    this.setState({ timeSupported: getTimeSupport() });
  }

  render() {
    const { date, value, onChange, hasError, ...rest } = this.props;

    const datetimeProps = {
      ...rest,
      autoComplete: 'off'
    };
    const timeValue = value ? parseDateParts(value).timeNoMs : '';
    return this.state.timeSupported ? (
      <Input
        type="time"
        hasError={hasError}
        value={timeValue}
        {...datetimeProps}
        onChange={e => {
          const time = isEmpty(e.currentTarget.value)
            ? null
            : withSeconds(e.currentTarget.value);
          if (time) {
            const newValue = combineFormDateTime(date, time);
            onChange(newValue);
          } else {
            onChange(null);
          }
        }}
      />
    ) : (
      <TimeInput
        value={isEmpty(timeValue) ? null : timeValue}
        locale={USER_LOCALE}
        onChange={v => {
          if (v) {
            const time = v || '';
            const newValue = combineFormDateTime(date, time);
            onChange(newValue);
          } else {
            onChange(null);
          }
        }}
        datetimeProps={datetimeProps}
      />
    );
  }
}
